import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Slider, Button, Typography } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  aspect?: number;
  onCancel: () => void;
  onCropDone: (file: File, previewUrl: string) => void;
}

interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

async function getCroppedFile(
  imageSrc: string,
  pixelCrop: CroppedAreaPixels,
): Promise<{ file: File; url: string }> {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise<void>((resolve) => { image.onload = () => resolve(); });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const file = new File([blob!], 'cropped.png', { type: 'image/png' });
      resolve({ file, url: URL.createObjectURL(blob!) });
    }, 'image/png');
  });
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ open, imageSrc, aspect = 1, onCancel, onCropDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback((_: unknown, pixels: CroppedAreaPixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleDone = async () => {
    if (!croppedAreaPixels) return;
    setLoading(true);
    try {
      const { file, url } = await getCroppedFile(imageSrc, croppedAreaPixels);
      onCropDone(file, url);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="Edit Image"
      centered
      width={520}
      maskClosable={false}
      zIndex={1500}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel} disabled={loading}>Cancel</Button>
          <Button type="primary" onClick={handleDone} loading={loading} style={{ background: '#2D6CCA' }}>
            Apply & Save
          </Button>
        </div>
      }
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      {/* Crop area */}
      <div style={{ position: 'relative', width: '100%', height: 460, background: 'transparent' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          minZoom={0.5}
          aspect={aspect}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          restrictPosition={false}
          style={{ containerStyle: { background: 'transparent' } }}
        />
      </div>

      {/* Zoom control */}
      <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Text type="secondary" style={{ minWidth: 54, fontSize: 12 }}>Zoom</Text>
        <ZoomOutOutlined style={{ fontSize: 14, color: '#888' }} />
        <Slider
          min={1} max={3} step={0.05} value={zoom}
          onChange={(v) => setZoom(v)}
          style={{ flex: 1 }}
          tooltip={{ formatter: (v) => `${Math.round((v ?? 1) * 100)}%` }}
        />
        <ZoomInOutlined style={{ fontSize: 14, color: '#888' }} />
        <Text type="secondary" style={{ minWidth: 38, textAlign: 'right', fontSize: 12 }}>
          {Math.round(zoom * 100)}%
        </Text>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
